import {Util} from './Util';
import {JiraWorklog, JiraWorklogWithDates, WorklogInput} from '../types/api';
import {AppSettings, Favorite} from '../types/storage';
import {JiraIssue} from '../types/components';
import {ApiClient} from './ApiClient';

// Jira Client (TypeScript)
export class JiraClient extends ApiClient {

  constructor() {
    super();
  }

  get base(): string {
    return `/api/jira`;
  }

  async login(email: string, token: string): Promise<string | null> {
    const url = `/api/auth/login`;
    const res = await super.fetch(url, 'POST', {email, token});
    if (
      res &&
      typeof res === 'object' &&
      Object.prototype.hasOwnProperty.call(res, 'token') &&
      typeof res.token === 'string'
    ) {
      super.updateAuthToken(res.token);

      return res.token;
    }

    return null;
  }

  async getUser(): Promise<any> {
    const url = `${this.base}/rest/api/3/myself`;
    const res = await super.fetch(url, 'GET');
    return res;
  }

  async getUserSettings(): Promise<AppSettings | null> {
    const url = `/api/user/settings`;
    const res = await super.fetch(url, 'GET');
    if (res && typeof res === 'object') {
      return {
        lang: Object.prototype.hasOwnProperty.call(res, 'locale') && typeof res.locale === 'string' ? res.locale : 'en',
        autoSync: Object.prototype.hasOwnProperty.call(res, 'autoSync') && typeof res.autoSync === 'boolean' ? res.autoSync : false,
        theme: Object.prototype.hasOwnProperty.call(res, 'theme') && res.theme === 1 ? 'dark' : 'light',
      } as AppSettings;
    }

    return null;
  }

  async updateUserSettings(settings: AppSettings): Promise<void> {
    const url = `/api/user/settings`;
    await super.fetch(url, 'POST', {
      locale: settings.lang,
      theme: settings.theme === 'dark' ? 1 : 0,
      autoSync: settings.autoSync
    });
  }

  async getUserFavorites(): Promise<Favorite[]> {
    const url = `/api/user/favorites`;
    const res = await super.fetch(url, 'GET');
    if (res && Array.isArray(res)) {
      return res.map((it: any) => ({
        id: Object.prototype.hasOwnProperty.call(it, 'id') && typeof it.id === 'string' ? it.id : '',
        issueKey: Object.prototype.hasOwnProperty.call(it, 'issueKey') && typeof it.issueKey === 'string' ? it.issueKey : '',
        name: Object.prototype.hasOwnProperty.call(it, 'title') && typeof it.title === 'string' ? it.title : undefined,
        description: Object.prototype.hasOwnProperty.call(it, 'description') && typeof it.description === 'string' ? it.description : undefined,
        color: Object.prototype.hasOwnProperty.call(it, 'color') && typeof it.color === 'string' ? it.color : undefined,
      }));
    }

    return [];
  }

  async updateUserFavorites(favorites: Favorite[]): Promise<void> {
    const url = `/api/user/favorites`;
    await super.fetch(url, 'POST', {
      favorites: favorites.map(fav => ({
        id: fav.id,
        issueKey: fav.issueKey,
        title: fav.name,
        description: fav.description,
        color: fav.color
      }))
    });
  }

  async searchIssues(query: string, exact?: boolean): Promise<JiraIssue[]> {
    if (!query) return [];
    // Escape quotes and append trailing wildcard for better prefix matches on short inputs
    const term = query.trim().replace(/["\\]/g, '\\$&');
    const termWithWildcard = term.endsWith('*') ? term : `${term}*`;
    const termWithoutWildcard = termWithWildcard.slice(0, -1);
    const jql = !exact ?
      `key ~ "${termWithWildcard}" OR summary ~ "${termWithWildcard}" order by updated DESC` :
      `key = "${termWithoutWildcard}" order by updated DESC`;

    // Use enhanced JQL endpoint
    const url = `${this.base}/rest/api/3/search/jql`;
    const body = {
      jql,
      fields: ['summary'],
      maxResults: 5
    };
    const data = await super.fetch(url, 'POST', body);

    const issues = Array.isArray(data.issues) ? data.issues : (Array.isArray(data.results) ? data.results : []);
    return (issues || []).map((it: any) => ({
      key: it.key,
      summary: it.fields && it.fields.summary
    }));
  }

  async upsertWorklog(worklog: WorklogInput & { jiraId?: string | null }): Promise<JiraWorklogWithDates> {
    // If we have a Jira worklog id, try to update. If not found (404), fall back to create.
    if (worklog.jiraId) {
      try {
        return await this.updateWorklog(worklog.issueKey, worklog.jiraId, worklog);
      } catch (e: any) {
        const msg = String(e?.message || '');
        // If the worklog no longer exists or cannot be updated (e.g., moved/removed), create a new one.
        if (msg.startsWith('404')) {
          return await this.uploadWorklog(worklog);
        }
        throw e;
      }
    }
    // No Jira ID => create
    return this.uploadWorklog(worklog);
  }

  async deleteWorklog(issueKey: string, worklogId: string): Promise<any> {
    const url = `${this.base}/rest/api/3/issue/${encodeURIComponent(issueKey)}/worklog/${encodeURIComponent(worklogId)}`;
    const data = await super.fetch(url, 'DELETE');
    return data;
  }

  private async uploadWorklog(worklog: WorklogInput): Promise<JiraWorklogWithDates> {
    // Round to nearest minute
    const timeSpentSec = Math.ceil(worklog.durationSec / 60) * 60;
    // min 60 sec, max 18 hours
    const timeSpentSeconds = Math.max(60, Math.min(18 * 3600, timeSpentSec));

    const url = `${this.base}/rest/api/3/issue/${encodeURIComponent(worklog.issueKey)}/worklog`;
    const body = {
      comment: {
        type: 'doc',
        version: 1,
        content: [
          {
            type: 'paragraph',
            content: [
              {type: 'text', text: worklog.description || ''}
            ]
          }
        ]
      },
      started: Util.formatJiraDateTimeLocal(worklog.start),
      timeSpentSeconds
    };

    const data = await super.fetch(url, 'POST', body);

    return this.mapJiraWorklog(data, worklog);
  }

  private async updateWorklog(issueKey: string, worklogId: string, worklog: WorklogInput): Promise<JiraWorklogWithDates> {
    // Round to nearest minute
    const timeSpentSec = Math.ceil(worklog.durationSec / 60) * 60;
    // min 60 sec, max 18 hours
    const timeSpentSeconds = Math.max(60, Math.min(18 * 3600, timeSpentSec));

    const url = `${this.base}/rest/api/3/issue/${encodeURIComponent(issueKey)}/worklog/${encodeURIComponent(worklogId)}`;
    const body = {
      comment: {
        type: 'doc',
        version: 1,
        content: [
          {
            type: 'paragraph',
            content: [
              {type: 'text', text: worklog.description || ''}
            ]
          }
        ]
      },
      started: Util.formatJiraDateTimeLocal(worklog.start),
      timeSpentSeconds
    };
    const data = await super.fetch(url, 'PUT', body);

    return this.mapJiraWorklog(data, worklog);
  }

  private mapJiraWorklog(data: JiraWorklog, worklog: WorklogInput): JiraWorklogWithDates {
    const start = data.started ? new Date(data.started) : worklog.start;
    const durationSec = data.timeSpentSeconds || worklog.durationSec;
    const end = new Date(start.getTime() + durationSec * 1000);

    return {...data, start, end, durationSec};
  }
}